import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "hideOnEscape", "autoFocusOnShow", "hideOnHoverOutside"],
    _excluded2 = ["aria-labelledby"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState, useEffect } from 'react';
import { hasFocusWithin } from 'ariakit-utils/focus';
import { useBooleanEvent, useEvent } from 'ariakit-utils/hooks';
import { useStore } from 'ariakit-utils/store';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useHovercard } from '../hovercard/hovercard.js';
import { M as MenuContext, a as MenuBarContext } from '../__utils-07f4a93f.js';
import { useMenuList } from './menu-list.js';

function getItemRefById(items, id) {
  var _items$find;

  if (!id) return;
  return (_items$find = items.find(function (item) {
    return item.id === id;
  })) == null ? void 0 : _items$find.ref;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a dropdown menu element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenu({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Role {...props}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Role>
 * ```
 */


var useMenu = createHook(function (_ref) {
  var state = _ref.state,
      _ref$hideOnEscape = _ref.hideOnEscape,
      hideOnEscape = _ref$hideOnEscape === void 0 ? true : _ref$hideOnEscape,
      _ref$autoFocusOnShow = _ref.autoFocusOnShow,
      autoFocusOnShow = _ref$autoFocusOnShow === void 0 ? true : _ref$autoFocusOnShow,
      _hideOnHoverOutside = _ref.hideOnHoverOutside,
      props = _objectWithoutProperties(_ref, _excluded);

  var parentMenu = useStore(MenuContext, []);
  var parentMenuBar = useStore(MenuBarContext, []);
  var hasParentMenu = !!parentMenu;
  var parentIsMenuBar = !!parentMenuBar && !hasParentMenu;
  var onKeyDownProp = props.onKeyDown;
  var hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;

    if (event.key === "Escape") {
      if (!hideOnEscapeProp(event)) return;

      if (!hasParentMenu) {
        // On Esc, only stop propagation if there's no parent menu. Otherwise,
        // pressing Esc should close all menus
        event.stopPropagation();
      }

      return state.hide();
    }
  });
  props = _objectSpread(_objectSpread({}, props), {}, {
    onKeyDown: onKeyDown
  }); // The aria-labelledby prop on MenuList defaults to the MenuButton's id. On
  // Dialog/Popover/Hovercard/Menu, we need to consider MenuHeading as well
  // and it should take precedence. That's why we need to destructure this
  // prop here and check if aria-labelledby is set later.

  var _useMenuList = useMenuList(_objectSpread({
    state: state
  }, props)),
      ariaLabelledBy = _useMenuList["aria-labelledby"],
      menuListProps = _objectWithoutProperties(_useMenuList, _excluded2);

  props = menuListProps;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      initialFocusRef = _useState2[0],
      setInitialFocusRef = _useState2[1]; // Sets the initial focus ref.


  useEffect(function () {
    var cleaning = false;
    setInitialFocusRef(function (prevInitialFocusRef) {
      if (cleaning) return;
      if (!state.autoFocusOnShow) return;
      if (prevInitialFocusRef) return prevInitialFocusRef;

      switch (state.initialFocus) {
        case "first":
          return getItemRefById(state.items, state.first());

        case "last":
          return getItemRefById(state.items, state.last());

        default:
          return state.baseRef;
      }
    });
    return function () {
      cleaning = true;
    };
  }, [state.autoFocusOnShow, state.initialFocus, state.items, state.first, state.last, state.baseRef]);
  var mayAutoFocusOnShow = !!autoFocusOnShow; // When the `autoFocusOnShow` prop is set to `true` (default), we'll only
  // move focus to the menu when there's an initialFocusRef set or the menu is
  // modal. Otherwise, users would have to manually call
  // state.setAutoFocusOnShow(true) every time they want to open the menu.
  // This differs from the usual dialog behavior that would automatically
  // focus on the dialog container when no initialFocusRef is set.

  var canAutoFocusOnShow = !!initialFocusRef || !!props.initialFocusRef || !!props.modal;
  props = useHovercard(_objectSpread(_objectSpread({
    state: state,
    initialFocusRef: initialFocusRef,
    autoFocusOnShow: mayAutoFocusOnShow ? canAutoFocusOnShow && autoFocusOnShow : state.autoFocusOnShow || !!props.modal
  }, props), {}, {
    hideOnHoverOutside: function hideOnHoverOutside(event) {
      if (typeof _hideOnHoverOutside === "function") {
        return _hideOnHoverOutside(event);
      }

      if (_hideOnHoverOutside != null) return _hideOnHoverOutside;

      if (hasParentMenu) {
        parentMenu.setActiveId(null);
        return true;
      }

      if (!parentIsMenuBar) return false;
      var disclosure = state.disclosureRef.current;
      if (!disclosure) return true;
      if (hasFocusWithin(disclosure)) return false;
      return true;
    },
    // If it's a submenu, it shouldn't behave like a modal dialog, nor display
    // a backdrop.
    modal: hasParentMenu ? false : props.modal,
    backdrop: hasParentMenu ? false : props.backdrop,
    // If it's a submenu, hide on esc will be handled differently. That is,
    // event.stopPropagation() won't be called, so the parent menus will also
    // be closed.
    hideOnEscape: hasParentMenu ? false : hideOnEscape
  }));
  props = _objectSpread({
    "aria-labelledby": ariaLabelledBy
  }, props);
  return props;
});
/**
 * A component that renders a dropdown menu element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

var Menu = createComponent(function (props) {
  var htmlProps = useMenu(props);
  return createElement("div", htmlProps);
});
export { Menu, useMenu };